/*
 expressionprofileTSV service is replaced with this visualizationTSVservice as all the methods
 are used by other visualization components also
 */
import { Injectable } from "@angular/core";
import { map, min as _min, max as _max, flatten, orderBy, startsWith, first } from "lodash-es";
import TSVFile from "../../model/tsv/TSVFile";
import TSVHeaders from "../../model/tsv/TSVHeaders";
import TSVRow from "../../model/tsv/TSVRow";
import DomainBoundaries from "../../views/sessions/session/visualization/expression-profile/domainboundaries";
import GeneExpression from "../../views/sessions/session/visualization/expression-profile/geneexpression";
import { PlotData } from "../../views/sessions/session/visualization/model/plotData";
import { SelectionRow } from "../../views/sessions/session/visualization/model/selectionRow";

@Injectable()
export class VisualizationTSVService {
  /*
   * @description: does tsv-file contain .chip-headers
   */
  public containsChipHeaders(tsv: TSVFile): boolean {
    return this.getChipHeaderIndexes(tsv.headers).length > 0;
  }

  /*
   * Get chipvalues from raw data
   */
  public getGeneExpressions(tsv: TSVFile): Array<GeneExpression> {
    const chipIndexes = this.getChipHeaderIndexes(tsv.headers);
    return map(tsv.body.rows, (row: TSVRow) => this.getGeneExpressionsByIndex(row, chipIndexes));
  }

  /*
   * max & min value from two-dimensional array
   */
  public getDomainBoundaries(tsv: TSVFile): DomainBoundaries {
    const chipIndexes = this.getChipHeaderIndexes(tsv.headers);
    const values = map(tsv.body.rows, (row: TSVRow) => row.getCellsByIndexes(chipIndexes));
    const flatValues = map(flatten(values), (value: string) => parseFloat(value));
    const min = _min(flatValues);
    const max = _max(flatValues);
    return new DomainBoundaries(min, max);
  }

  /*
   * Return array containing numbers indicating indexes for column headers starting with 'chip.'
   */
  public getChipHeaderIndexes(tsvHeaders: TSVHeaders): Array<number> {
    return tsvHeaders.headers
      .map((cell: string, index: number) => (startsWith(cell, "chip.") ? index : -1))
      .filter((cell: number) => cell !== -1);
  }

  /*
   * create new GeneExpression from data with given id
   */
  public getGeneExpression(tsv: TSVFile, id: string): GeneExpression {
    const chipIndexes = this.getChipHeaderIndexes(tsv.headers);
    const tsvRow = tsv.body.getTSVRow(id);
    return this.getGeneExpressionsByIndex(tsvRow, chipIndexes);
  }

  /*
   * create new GeneExpressions from data with the given ids
   *
   * The rows are looked up in one pass and the chip header indexes are searched
   * once, unlike in a loop of getGeneExpression(). The results are in the order
   * of the file, not in the order of the ids.
   */
  public getGeneExpressionsByIds(tsv: TSVFile, ids: Array<string>): Array<GeneExpression> {
    const chipIndexes = this.getChipHeaderIndexes(tsv.headers);
    return map(tsv.body.getTSVRows(ids), (row: TSVRow) => this.getGeneExpressionsByIndex(row, chipIndexes));
  }

  /*
   * Return a single GeneExpression based on id for the TSVRow and the values in indexes of row
   */
  getGeneExpressionsByIndex(row: TSVRow, indexes: Array<number>): GeneExpression {
    const values = row.getCellsByIndexes(indexes);
    const numberValues = map(values, (value: string) => parseFloat(value));
    return new GeneExpression(row.id, numberValues);
  }

  /*
   * Order body by first chip-value in each row
   */
  public orderBodyByFirstValue(geneExpressions: Array<GeneExpression>): Array<GeneExpression> {
    return orderBy(geneExpressions, [(geneExpression: GeneExpression) => first(geneExpression.values)]);
  }

  /*
   * Get chip-value headers
   */
  public getChipHeaders(tsv: TSVFile): Array<string> {
    const chipHeaderIndexes = this.getChipHeaderIndexes(tsv.headers);
    return tsv.headers.getItemsByIndexes(chipHeaderIndexes);
  }

  /*
   * @description: does tsv-file contain a symbol column
   */
  public containsSymbolColumn(tsv: TSVFile): boolean {
    return tsv.getColumnIndex("symbol") !== -1;
  }

  /*
   * @description: symbol and identifier of the given rows, for the selection list next to a plot
   *
   * The symbol is left empty in files without a symbol column, see containsSymbolColumn().
   * Files without an identifier column fall back to the first column, which is the identifier
   * in files where the header row doesn't name it.
   */
  public getSelectionRows(tsv: TSVFile, rowIds: Array<string>): Array<SelectionRow> {
    return this.getSelectionRowsFromTSVRows(tsv, tsv.body.getTSVRows(rowIds));
  }

  /**
   * @description selection list of rows that have already been looked up
   *
   * Saves a pass over the body when the caller has the rows at hand.
   */
  public getSelectionRowsFromTSVRows(tsv: TSVFile, rows: Array<TSVRow>): Array<SelectionRow> {
    const symbolIndex = tsv.getColumnIndex("symbol");
    const identifierIndex = tsv.getColumnIndex("identifier");
    return rows.map((row: TSVRow) => ({
      symbol: symbolIndex !== -1 ? row.row[symbolIndex] : null,
      identifier: identifierIndex !== -1 ? row.row[identifierIndex] : row.row[0],
    }));
  }

  public getMinY(plotData: Array<PlotData>): number {
    return plotData.reduce((min, p) => (p.plotPoint.y < min ? p.plotPoint.y : min), plotData[0].plotPoint.y);
  }
  public getMaxY(plotData: Array<PlotData>): number {
    return plotData.reduce((max, p) => (p.plotPoint.y > max ? p.plotPoint.y : max), plotData[0].plotPoint.y);
  }

  public getMinX(plotData: Array<PlotData>): number {
    return plotData.reduce((min, p) => (p.plotPoint.x < min ? p.plotPoint.x : min), plotData[0].plotPoint.x);
  }
  public getMaxX(plotData: Array<PlotData>): number {
    return plotData.reduce((max, p) => (p.plotPoint.x > max ? p.plotPoint.x : max), plotData[0].plotPoint.x);
  }
}
